import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, ArrowRight, Home, MessageSquare } from "lucide-react";
import Header from "../../components/layout/Header";
import Footer from "../../components/layout/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30, filter: "blur(6px)" },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] },
  }),
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.6, ease: [0.34, 1.56, 0.64, 1] },
  },
};

/* ─── SUCCESS PAGE ─── */
const ContactSuccessPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect to home after 10 seconds
    const timer = setTimeout(() => {
      navigate("/");
    }, 10000);

    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#FAFAFE] flex flex-col">
      <Header />

      <div className="flex-1 flex items-center justify-center py-20 sm:py-32 px-4">
        <div className="w-full max-w-2xl">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              visible: {
                transition: { staggerChildren: 0.15 },
              },
            }}
            className="text-center"
          >
            {/* Success Icon */}
            <motion.div variants={scaleIn} className="mb-8 flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full blur-2xl opacity-30 animate-pulse" />
                <CheckCircle2 className="w-24 h-24 text-emerald-600 relative z-10" />
              </div>
            </motion.div>

            {/* Main Message */}
            <motion.h1
              variants={fadeUp}
              className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-900 mb-4 leading-tight"
            >
              Thank You!
            </motion.h1>

            <motion.p
              variants={fadeUp}
              custom={1}
              className="text-lg sm:text-xl text-slate-600 mb-6 leading-relaxed"
            >
              We've received your message and we're excited to connect with you.
            </motion.p>

            {/* What Happens Next */}
            <motion.div
              variants={fadeUp}
              custom={2}
              className="glass-strong rounded-2xl p-8 mb-8 shadow-apple text-left max-w-lg mx-auto"
            >
              <h3 className="text-lg font-bold text-slate-900 mb-4">
                What happens next?
              </h3>
              <div className="space-y-4">
                {[
                  "Our team reviews your inquiry within 2 hours",
                  "We'll reach out via your preferred contact method",
                  "First consultation is completely free",
                  "No pressure, no obligation",
                ].map((step, i) => (
                  <div key={i} className="flex gap-3 items-start">
                    <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-1">
                      <div className="w-2 h-2 rounded-full bg-emerald-600" />
                    </div>
                    <p className="text-slate-600 pt-0.5">{step}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Email Confirmation */}
            <motion.div
              variants={fadeUp}
              custom={3}
              className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8 text-sm text-slate-700"
            >
              <p>
                📧 Check your email for a confirmation. If you don't see it,
                check your spam folder.
              </p>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              variants={fadeUp}
              custom={4}
              className="flex flex-col sm:flex-row gap-4 justify-center mb-8"
            >
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold hover:shadow-apple-lg transition-all"
              >
                <Home className="w-4 h-4" />
                Back to Home
              </Link>
              <a
                href="https://wa.me/917004771388?text=Hi%20BusiGrow%2C%20I%27m%20following%20up%20on%20my%20inquiry."
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl border border-emerald-300 text-emerald-700 font-semibold hover:bg-emerald-50 transition-all"
              >
                <MessageSquare className="w-4 h-4" />
                Chat on WhatsApp
              </a>
            </motion.div>

            {/* Auto-redirect message */}
            <motion.p
              variants={fadeUp}
              custom={5}
              className="text-sm text-slate-500"
            >
              Redirecting to home in 10 seconds...
            </motion.p>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ContactSuccessPage;
